import * as React from 'react';

import { DescriptionPage_SelfServeConfiguration_AboutModule_SubsectionName as AboutModuleSubsectionName } from '__generated__/graphql-types';

import { useIsEnterpriseAdminAbleToAssignContent } from 'bundles/enterprise-admin/useIsEnterpriseAdminAbleToAssignContent';
import type { InjectedNaptime } from 'bundles/naptimejs';
import LearnerIncentiveModuleRenderer from 'bundles/unified-description-page-common/module-renderers/LearnerIncentiveModuleRenderer';
import ReviewsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ReviewsModuleRenderer';
import TestimonialsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/TestimonialsModuleRenderer';
import ThinBannerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ThinBannerModuleRenderer';
import AiDisclaimerModuleRenderer from 'bundles/unified-description-page-common/module-renderers/ai-disclaimer/AiDisclaimerModuleRenderer';
import EnterpriseBreadcrumbsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/breadcrumbs/EnterpriseCourseBreadcrumbsModuleRenderer';
import CareerOutcomesModuleRenderer from 'bundles/unified-description-page-common/module-renderers/career-outcomes/CourseCareerOutcomesModuleRenderer';
import ProductOverviewModuleRenderer from 'bundles/unified-description-page-common/module-renderers/product-overview/EnterpriseCourseProductOverviewModuleRenderer';
import EnterpriseCourseRecommendtaionsModuleRenderer from 'bundles/unified-description-page-common/module-renderers/recommendations/EnterpriseCourseRecommendationsModuleRenderer';
import CourseSyllabusModuleRenderer from 'bundles/unified-description-page-common/module-renderers/syllabus/CourseSyllabusModuleRenderer';
import type { EnterpriseCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/EnterpriseCoursePageQuery';
import {
  KeyInformationItemNames,
  ModuleNames,
  ProductDetailItemNames,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import type {
  PageConfig,
  ProductOverviewModuleConfig,
} from 'bundles/unified-description-page-common/types/moduleConfig';
import { getAudienceLanguageCode } from 'bundles/unified-description-page-common/utils/dubbingUtil';

import CoursesHeroImage from 'images/unified-description-page/courses-hero-image.png';

type Props = {
  pageData: EnterpriseCoursePageFieldsFragment;
  pageConfig?: PageConfig | null;
  naptime: InjectedNaptime;
};

const Renderer = ({ pageData, pageConfig, naptime }: Props) => {
  const isEnterpriseAdminAbleToAssignContent = useIsEnterpriseAdminAbleToAssignContent({ ssr: false });

  if (!pageConfig || !pageConfig.modules.length) {
    return null;
  }

  const modulesToRender = pageConfig.modules.map((moduleConfig) => {
    switch (moduleConfig.name) {
      case ModuleNames.ProductOverview:
        return (
          <ProductOverviewModuleRenderer
            pageData={pageData}
            config={
              {
                ...moduleConfig,
                // visibility of hero language info depends on XDP product AI dubbing, user site language
                showHeroLanguageInfo:
                  pageData.dubbedLanguages?.length && getAudienceLanguageCode(pageData.dubbedLanguages),
                backgroundImage: CoursesHeroImage,
                showAssignButton: isEnterpriseAdminAbleToAssignContent,
                keyInfoModule: {
                  items: [
                    { name: KeyInformationItemNames.ContentCount },
                    { name: KeyInformationItemNames.Reviews },
                    { name: KeyInformationItemNames.Difficulty },
                    { name: KeyInformationItemNames.Duration },
                    { name: KeyInformationItemNames.Schedule },
                    { name: KeyInformationItemNames.CourseSatisfactionScore },
                    { name: KeyInformationItemNames.EnterpriseCredit },
                  ],
                },
                aboutModule: {
                  subsections: [
                    { name: AboutModuleSubsectionName.LearningOutcomes },
                    { name: AboutModuleSubsectionName.Skills },
                    { name: AboutModuleSubsectionName.DetailsToKnow },
                  ],
                },
                productDetailItems: [
                  ProductDetailItemNames.ShareableCertificate,
                  ProductDetailItemNames.Exclusive,
                  ProductDetailItemNames.SyllabusContent,
                  ProductDetailItemNames.LanguagesWithMLInfo,
                  ProductDetailItemNames.RecentlyUpdated,
                ],
              } as ProductOverviewModuleConfig
            }
            key={moduleConfig.name}
          />
        );
      case ModuleNames.CareerOutcomes:
        return <CareerOutcomesModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.LearnerIncentive:
        return <LearnerIncentiveModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Syllabus:
        return <CourseSyllabusModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Recommendations:
        return <EnterpriseCourseRecommendtaionsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Reviews:
        return <ReviewsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.Testimonials:
        return <TestimonialsModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      case ModuleNames.ThinBanner:
        return <ThinBannerModuleRenderer key={moduleConfig.name} />;
      case ModuleNames.Breadcrumbs:
        return <EnterpriseBreadcrumbsModuleRenderer pageData={pageData} key={moduleConfig.name} naptime={naptime} />;
      case ModuleNames.AiDisclaimer:
        return <AiDisclaimerModuleRenderer pageData={pageData} key={moduleConfig.name} />;
      default:
        return null;
    }
  });

  return <>{modulesToRender}</>;
};

export default Renderer;
